import { SubscriptionDiscount } from "./types/billing";

export enum FLAT_RATE_PLANS {
    FREE = "FREE",
    BASIC = "BASIC",
    PLUS = "PLUS",
    ADVANCED = "ADVANCED",
    ENTERPRISE = "ENTERPRISE",
    FRESHBOOKS = "FRESHBOOKS",
    READ_ONLY = "READ_ONLY",
    TAX = "TAX",
}

export enum PLAN_CATALOG_VERSION {
    V1 = 1,
    V2 = 2,
    V3 = 3,
}

export enum PREMIUM_FEATURES {
    UNLIMITED_ENTITIES = "UNLIMITED_ENTITIES",
    CLASSES = "CLASSES",
    IMPORTER = "IMPORTER",
    RECONCILIATION = "RECONCILIATION",
    AP_RECONCILIATION = "AP_RECONCILIATION",
    CONSOLIDATION = "CONSOLIDATION",
    SCHEDULES = "SCHEDULES",
    FIXED_ASSETS = "FIXED_ASSETS",
    ACCRUAL_LEDGER = "ACCRUAL_LEDGER",
    DOCUMENT_CLASSIFICATION = "DOCUMENT_CLASSIFICATION",
    GUSTO = "GUSTO",
    PLAID_STATEMENTS = "PLAID_STATEMENTS",
    INVOICE_PAYMENT_COLLECTION = "INVOICE_PAYMENT_COLLECTION",
    INVOICES = "INVOICES",
    INTEGRATIONS = "INTEGRATIONS",
    AGENT_ACCESS = "AGENT_ACCESS",
}

export const flatRatePlanNames = {
    [FLAT_RATE_PLANS.BASIC]: "Basic",
    [FLAT_RATE_PLANS.PLUS]: "Plus",
    [FLAT_RATE_PLANS.ADVANCED]: "Advanced",
    [FLAT_RATE_PLANS.ENTERPRISE]: "Enterprise",
    [FLAT_RATE_PLANS.FRESHBOOKS]: "FreshBooks",
    [FLAT_RATE_PLANS.FREE]: "Free",
    [FLAT_RATE_PLANS.READ_ONLY]: "Read-only",
    [FLAT_RATE_PLANS.TAX]: "Tax",
} as const satisfies Record<FLAT_RATE_PLANS, string>;

/**
 * The one sentence a Read-only workspace refuses an organization-token write
 * with on the Platform API. Kept next to the plan enum for the same reason
 * `VIEW_ONLY_WRITE_REFUSAL_MESSAGE` lives beside the role enum: the refusal is
 * part of the plan's contract, and tests pin the exact wording.
 */
export const READ_ONLY_PLAN_WRITE_REFUSAL_MESSAGE =
    "This workspace is on the Read-only plan. It cannot be modified through the Platform API.";

/**
 * Org client-license plans that bill through the Plus product pool.
 * ENTERPRISE and FRESHBOOKS roll up into Plus on the org dispatch path.
 * ADVANCED has its own product pool and is not included here.
 */
export const PLUS_CLIENT_PLANS: readonly FLAT_RATE_PLANS[] = [
    FLAT_RATE_PLANS.PLUS,
    FLAT_RATE_PLANS.ENTERPRISE,
    FLAT_RATE_PLANS.FRESHBOOKS,
] as const;

/**
 * Plans that participate in per-entity metered billing. FREE and BASIC are
 * excluded. Advanced bills additional entities on the same rule as Plus
 * (first qualifying entity included, later threshold-crossing entities
 * billed) while keeping its own client-license product. Single source of
 * truth shared by the workspace-level counter
 * (`MeteredEntityCountService`), the org-level aggregator
 * (`OrgMeteredBillingService`), and the upgrade-modal UI.
 */
export const METERED_PLANS: readonly FLAT_RATE_PLANS[] = [
    ...PLUS_CLIENT_PLANS,
    FLAT_RATE_PLANS.ADVANCED,
] as const;

export interface FlatRatePrice {
    total: number;
    totalBeforeDiscount: number;
    discount: SubscriptionDiscount | null;
    invalidDiscountCode: boolean;
    nextBillingDate: Date;
}

export const TRIAL_LENGTH_DAYS = 14;
export const PAID_PLAN_USAGE_LIMIT = 250_000;
/** Transaction count threshold above which free-tier v2 users are prompted to upgrade. */
export const ENTITY_TRANSACTIONS_THRESHOLD = 250;

/**
 * Free-workspace LLM-enrichment trial + quota, counted in transactions enriched
 * (not raw LLM calls). A new free workspace gets a genuinely unlimited LLM trial
 * while younger than {@link FREE_LLM_ENRICHMENT_QUOTA_NEW_WS_AGE_DAYS} (behaving
 * like a paid workspace, consuming no quota) — extendable to
 * {@link FREE_LLM_TRIAL_EXTENDED_WINDOW_DAYS} total via the
 * `new-workspace-extended-llm-trial` flag. Past the window a free workspace is
 * "established": workspace-level LLM is denied and per-transaction LLM is capped
 * at {@link FREE_LLM_ENRICHMENT_QUOTA_ESTABLISHED_WS_MONTHLY}/month. Paid
 * workspaces have no quota. The read-side {@link ENTITY_TRANSACTIONS_THRESHOLD}
 * (newest 250 visible) is a separate, unrelated limit.
 */
export const FREE_LLM_ENRICHMENT_QUOTA_ESTABLISHED_WS_MONTHLY = 50;
export const FREE_LLM_ENRICHMENT_QUOTA_NEW_WS_AGE_DAYS = 30;
export const FREE_LLM_TRIAL_EXTENDED_WINDOW_DAYS = 60;

/**
 * Kill switch for the free-workspace LLM enrichment quota. While
 * `isEnforced` is false, `FreeEnrichmentLlmGuard` allows every call —
 * established free workspaces get full, uncapped enrichment, identical to
 * paid workspaces — and the billing-status API reports no quota. Flip to
 * true and deploy to restore the quota rules above exactly as before; the
 * Redis monthly quota state is independent of this switch and resumes where
 * it left off. An object rather than a bare boolean so tests can
 * `jest.replaceProperty` it.
 */
export const FREE_LLM_ENRICHMENT_QUOTA_ENFORCEMENT = { isEnforced: false };
export const FREE_MONTHS_FOR_TRIALING = 3;
export const FREE_MONTHS_FOR_FREEMIUM = 3;
export const ADDITIONAL_ENTITIES_METERED_PRODUCT_NAME = "Additional entities";
/**
 * Grace window during which an organization member may add additional entities
 * to a workspace whose `planBeforeTransfer` was Plus, even if the workspace's
 * current plan no longer grants `UNLIMITED_ENTITIES`.
 */
export const ADDITIONAL_ENTITY_GRACE_PERIOD_DAYS = 14;

export interface MeteredProduct {
    readonly name: string;
    readonly productId: string;
    readonly priceId: string;
    readonly intervalAmountCents: number;
}
export interface FlatRateProduct {
    readonly plan: FLAT_RATE_PLANS;
    readonly productId: string;
    readonly priceId: string;
    readonly billingInterval: number;
    readonly monthlyAmountCents: number;
    readonly intervalAmountCents: number;
    readonly features: PREMIUM_FEATURES[];
    readonly usageLimit: number;
    readonly meteredProducts?: MeteredProduct[];
}

export interface FlatRateBillingCatalog {
    products: FlatRateProduct[];
    freshbooksPlan: FlatRateProduct;
}

export interface FlatRateBillingConfig {
    defaultPlanCatalogVersion: PLAN_CATALOG_VERSION;
    catalogs: {
        [PLAN_CATALOG_VERSION.V1]: FlatRateBillingCatalog;
        [PLAN_CATALOG_VERSION.V2]: FlatRateBillingCatalog;
        [PLAN_CATALOG_VERSION.V3]: FlatRateBillingCatalog;
    };
}

function flatRatePlanProducts(
    config: FlatRateBillingConfig,
): FlatRateProduct[] {
    return Object.values(config.catalogs).flatMap((catalog) => [
        ...catalog.products,
        catalog.freshbooksPlan,
    ]);
}

/**
 * The plan a Stripe product sells, or null for the legacy products that linger
 * in Stripe with no place in the app's catalog.
 */
export function findPlanForProductId(
    config: FlatRateBillingConfig,
    stripeProductId: string,
): FLAT_RATE_PLANS | null {
    const product = flatRatePlanProducts(config).find(
        (candidate) => candidate.productId === stripeProductId,
    );

    return product?.plan ?? null;
}

/** Null for {@link FLAT_RATE_PLANS.FREE}, which is not sold as a product. */
export function findProductIdForPlan(
    config: FlatRateBillingConfig,
    plan: FLAT_RATE_PLANS,
): string | null {
    const product = flatRatePlanProducts(config).find(
        (candidate) => candidate.plan === plan,
    );

    return product?.productId ?? null;
}

/**
 * Base feature set included in each flat-rate plan.
 * Single source of truth for plan capabilities — used by both the
 * billing config provider (BE) and client-side plan-gate checks (FE/common).
 */
export const PLAN_BASE_FEATURES: Record<
    FLAT_RATE_PLANS,
    readonly PREMIUM_FEATURES[]
> = {
    [FLAT_RATE_PLANS.FREE]: [
        PREMIUM_FEATURES.INVOICES,
        PREMIUM_FEATURES.INTEGRATIONS,
    ],
    [FLAT_RATE_PLANS.BASIC]: [
        PREMIUM_FEATURES.DOCUMENT_CLASSIFICATION,
        PREMIUM_FEATURES.IMPORTER,
        PREMIUM_FEATURES.RECONCILIATION,
        PREMIUM_FEATURES.GUSTO,
        PREMIUM_FEATURES.PLAID_STATEMENTS,
        PREMIUM_FEATURES.INVOICE_PAYMENT_COLLECTION,
        PREMIUM_FEATURES.INVOICES,
        PREMIUM_FEATURES.INTEGRATIONS,
        PREMIUM_FEATURES.AGENT_ACCESS,
    ],
    // Tax is Basic without bookkeeping-side features. A copied literal, not a
    // shared reference, so a later edit to one plan cannot leak into the other.
    [FLAT_RATE_PLANS.TAX]: [
        PREMIUM_FEATURES.DOCUMENT_CLASSIFICATION,
        PREMIUM_FEATURES.IMPORTER,
        PREMIUM_FEATURES.RECONCILIATION,
        PREMIUM_FEATURES.AGENT_ACCESS,
    ],
    [FLAT_RATE_PLANS.PLUS]: [
        PREMIUM_FEATURES.DOCUMENT_CLASSIFICATION,
        PREMIUM_FEATURES.IMPORTER,
        PREMIUM_FEATURES.RECONCILIATION,
        PREMIUM_FEATURES.GUSTO,
        PREMIUM_FEATURES.PLAID_STATEMENTS,
        PREMIUM_FEATURES.INVOICE_PAYMENT_COLLECTION,
        PREMIUM_FEATURES.UNLIMITED_ENTITIES,
        PREMIUM_FEATURES.CLASSES,
        PREMIUM_FEATURES.ACCRUAL_LEDGER,
        PREMIUM_FEATURES.AP_RECONCILIATION,
        PREMIUM_FEATURES.INVOICES,
        PREMIUM_FEATURES.INTEGRATIONS,
        PREMIUM_FEATURES.AGENT_ACCESS,
    ],
    [FLAT_RATE_PLANS.ADVANCED]: [
        PREMIUM_FEATURES.DOCUMENT_CLASSIFICATION,
        PREMIUM_FEATURES.IMPORTER,
        PREMIUM_FEATURES.RECONCILIATION,
        PREMIUM_FEATURES.GUSTO,
        PREMIUM_FEATURES.PLAID_STATEMENTS,
        PREMIUM_FEATURES.INVOICE_PAYMENT_COLLECTION,
        PREMIUM_FEATURES.UNLIMITED_ENTITIES,
        PREMIUM_FEATURES.CLASSES,
        PREMIUM_FEATURES.ACCRUAL_LEDGER,
        PREMIUM_FEATURES.AP_RECONCILIATION,
        PREMIUM_FEATURES.CONSOLIDATION,
        PREMIUM_FEATURES.SCHEDULES,
        PREMIUM_FEATURES.FIXED_ASSETS,
        PREMIUM_FEATURES.INVOICES,
        PREMIUM_FEATURES.INTEGRATIONS,
        PREMIUM_FEATURES.AGENT_ACCESS,
    ],
    [FLAT_RATE_PLANS.ENTERPRISE]: [
        PREMIUM_FEATURES.DOCUMENT_CLASSIFICATION,
        PREMIUM_FEATURES.IMPORTER,
        PREMIUM_FEATURES.RECONCILIATION,
        PREMIUM_FEATURES.GUSTO,
        PREMIUM_FEATURES.PLAID_STATEMENTS,
        PREMIUM_FEATURES.INVOICE_PAYMENT_COLLECTION,
        PREMIUM_FEATURES.UNLIMITED_ENTITIES,
        PREMIUM_FEATURES.CLASSES,
        PREMIUM_FEATURES.ACCRUAL_LEDGER,
        PREMIUM_FEATURES.AP_RECONCILIATION,
        PREMIUM_FEATURES.CONSOLIDATION,
        PREMIUM_FEATURES.SCHEDULES,
        PREMIUM_FEATURES.FIXED_ASSETS,
        PREMIUM_FEATURES.INVOICES,
        PREMIUM_FEATURES.INTEGRATIONS,
        PREMIUM_FEATURES.AGENT_ACCESS,
    ],
    [FLAT_RATE_PLANS.FRESHBOOKS]: [
        PREMIUM_FEATURES.DOCUMENT_CLASSIFICATION,
        PREMIUM_FEATURES.IMPORTER,
        PREMIUM_FEATURES.RECONCILIATION,
        PREMIUM_FEATURES.GUSTO,
        PREMIUM_FEATURES.PLAID_STATEMENTS,
        PREMIUM_FEATURES.INVOICE_PAYMENT_COLLECTION,
        PREMIUM_FEATURES.UNLIMITED_ENTITIES,
        PREMIUM_FEATURES.CLASSES,
        PREMIUM_FEATURES.ACCRUAL_LEDGER,
        PREMIUM_FEATURES.AP_RECONCILIATION,
        PREMIUM_FEATURES.INVOICES,
        PREMIUM_FEATURES.INTEGRATIONS,
        PREMIUM_FEATURES.AGENT_ACCESS,
    ],
    // Read-only mirrors Plus's read capability (D14). A copied literal, not a
    // shared reference, so a later edit to one plan cannot leak into the other.
    [FLAT_RATE_PLANS.READ_ONLY]: [
        PREMIUM_FEATURES.DOCUMENT_CLASSIFICATION,
        PREMIUM_FEATURES.IMPORTER,
        PREMIUM_FEATURES.RECONCILIATION,
        PREMIUM_FEATURES.GUSTO,
        PREMIUM_FEATURES.PLAID_STATEMENTS,
        PREMIUM_FEATURES.INVOICE_PAYMENT_COLLECTION,
        PREMIUM_FEATURES.UNLIMITED_ENTITIES,
        PREMIUM_FEATURES.CLASSES,
        PREMIUM_FEATURES.ACCRUAL_LEDGER,
        PREMIUM_FEATURES.AP_RECONCILIATION,
        PREMIUM_FEATURES.INVOICES,
        PREMIUM_FEATURES.INTEGRATIONS,
        PREMIUM_FEATURES.AGENT_ACCESS,
    ],
};

export const DEFAULT_PLAN_CATALOG_VERSION = PLAN_CATALOG_VERSION.V1;

export function resolvePlanCatalogVersion(
    planCatalogVersion: number | null | undefined,
): PLAN_CATALOG_VERSION {
    if (planCatalogVersion === PLAN_CATALOG_VERSION.V3) {
        return PLAN_CATALOG_VERSION.V3;
    }

    if (planCatalogVersion === PLAN_CATALOG_VERSION.V2) {
        return PLAN_CATALOG_VERSION.V2;
    }

    return DEFAULT_PLAN_CATALOG_VERSION;
}

export function hasFreeTierTransactionThreshold(
    planCatalogVersion: PLAN_CATALOG_VERSION | null | undefined,
): boolean {
    return (
        planCatalogVersion === PLAN_CATALOG_VERSION.V2 ||
        planCatalogVersion === PLAN_CATALOG_VERSION.V3
    );
}
